import ReactGA from 'react-ga4';
import mixpanel from 'mixpanel-browser';

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
const META_PIXEL_ID = '1612638440184176';
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

let mixpanelReady = false;

/**
 * Initialize Google Analytics 4
 * Only initializes if MEASUREMENT_ID is set
 */
export const initGA = () => {
  if (MEASUREMENT_ID) {
    ReactGA.initialize(MEASUREMENT_ID);
  }
};

export const initMixpanel = () => {
  if (mixpanelReady || !MIXPANEL_TOKEN || typeof window === 'undefined') {
    return;
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === 'development',
    persistence: 'localStorage',
    record_sessions_percent: 100,
    record_network: true,
    record_mask_all_text: false,
    record_mask_text_selector: '', 
    record_block_selector: ''
  });

  mixpanelReady = true;
};

/**
 * Check if Meta Pixel is available
 */
const isMetaPixelAvailable = () => {
  return typeof window !== 'undefined' && window.fbq && META_PIXEL_ID;
};

const isMixpanelAvailable = () => {
  return typeof window !== 'undefined' && Boolean(MIXPANEL_TOKEN);
};

/**
 * Generate a unique event ID for deduplication between Pixel and CAPI
 */
const generateEventId = () => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Track a Meta Pixel event
 * @param {string} eventName - Standard or custom event name
 * @param {object} params - Event parameters
 * @param {string} eventId - Optional event ID for deduplication with CAPI
 */
export const trackMetaEvent = (eventName, params = {}, eventId = null) => {
  if (!isMetaPixelAvailable()) {
    console.log('[Meta Pixel] Not available, skipping event:', eventName);
    return null;
  }
  
  const eid = eventId || generateEventId();
  // eventID must be passed as 4th parameter (options object), not in custom data
  window.fbq('track', eventName, params, { eventID: eid });
  console.log('[Meta Pixel] Event fired:', eventName, params, 'eventID:', eid);
  return eid;
};

export const trackMixpanelEvent = (eventName, properties = {}) => {
  if (!isMixpanelAvailable()) {
    return;
  }

  if (!mixpanelReady) {
    initMixpanel();
  }

  if (!mixpanelReady) {
    return;
  }

  mixpanel.track(eventName, properties);
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title) => {
  ReactGA.send({ hitType: 'pageview', page: path, title });
  trackMixpanelEvent('page_viewed', { path, title });
};

/**
 * Track a custom event
 * @param {string} category - Event category (e.g., 'User Journey')
 * @param {string} action - Event action (e.g., 'file_uploaded')
 * @param {string} label - Event label (optional, e.g., file type)
 */
export const trackEvent = (category, action, label, properties = {}) => {
  ReactGA.event({ category, action, label });
  trackMixpanelEvent(action, {
    category,
    ...(label !== undefined ? { label } : {}),
    ...properties,
  });
};

// Pre-defined event tracking functions for the user journey
export const Analytics = {
  // Type selection
  trackTypeSelected: (bookType) => {
    trackEvent('User Journey', 'type_selected', bookType);
  },

  // File upload
  trackFileUploaded: (fileType, fileName = null, fileSizeBytes = null) => {
    trackEvent('User Journey', 'file_uploaded', fileType, {
      file_type: fileType,
      ...(fileName ? { file_name: fileName } : {}),
      ...(fileSizeBytes !== null ? { file_size_bytes: fileSizeBytes } : {}),
      source: 'upload_page',
    });
  },

  // Analysis
  trackAnalysisStarted: () => {
    trackEvent('User Journey', 'analysis_started');
  },
  trackAnalysisComplete: (storiesCount) => {
    trackEvent('User Journey', 'analysis_complete', `${storiesCount} stories`);
  },

  // Config
  trackConfigSaved: () => {
    trackEvent('User Journey', 'config_saved');
  },

  // Privacy
  trackPrivacySet: () => {
    trackEvent('User Journey', 'privacy_set');
  },

  // Book setup
  trackBookSetupComplete: () => {
    trackEvent('User Journey', 'book_setup_complete');
  },

  // Preview - also tracks Meta ViewContent
  trackPreviewViewed: () => {
    trackEvent('User Journey', 'preview_viewed');
    trackMetaEvent('ViewContent', { content_type: 'product', content_name: 'Book Preview' });
  },
  trackPdfDownloaded: () => {
    trackEvent('User Journey', 'pdf_downloaded');
  },

  // Story navigation
  trackStoryViewed: (storyIndex, storyTitle) => {
    trackEvent('Preview', 'story_viewed', `${storyIndex + 1}: ${storyTitle}`);
  },

  // Lead capture / conversion - also tracks Meta Lead
  trackBookNowClicked: () => {
    trackEvent('Conversion', 'book_now_clicked');
  },
  trackLeadSubmitted: (email) => {
    trackEvent('Conversion', 'lead_submitted', email);
    trackMetaEvent('Lead', { content_name: 'Email Capture' });
  },
  
  // Purchase flow - tracks Meta InitiateCheckout and Purchase
  trackBuyFullBookClicked: () => {
    trackEvent('Purchase', 'buy_full_book_clicked');
    trackMetaEvent('InitiateCheckout', { content_type: 'product', content_name: 'Digital Yearbook' });
  },
  /**
   * Track purchase completion
   * @param {number} price - Purchase price
   * @param {string} currency - Currency code (e.g., 'USD')
   * @returns {string|null} - Event ID for CAPI deduplication
   */
  trackPurchaseCompleted: (price, currency) => {
    trackEvent('Purchase', 'purchase_completed', `${price} ${currency}`);
    // Return event ID for CAPI deduplication
    return trackMetaEvent('Purchase', { 
      value: parseFloat(price), 
      currency: currency || 'USD',
      content_type: 'product',
      content_name: 'Digital Yearbook'
    });
  },
  trackFullBookGenerated: (chaptersCount) => {
    trackEvent('Purchase', 'full_book_generated', `${chaptersCount} chapters`);
  },
  trackFullBookDownloaded: () => {
    trackEvent('Purchase', 'full_book_downloaded');
  },
};

export default Analytics;
